import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { useDevMode } from '../context/DevModeContext';
import { useStagingTray } from '../context/StagingTrayContext';
import useTwitterSearch from '../hooks/useTwitterSearch';
import SearchBar from './ui/SearchBar';
import SearchControls from './ui/SearchControls';
import SearchStatsBar from './ui/SearchStatsBar';
import TweetList from './ui/TweetList';
import LoadingCard from './ui/LoadingCard';
import SkeletonCard from './ui/SkeletonCard';
import { isRoamSource } from './utils';
import './TwitterSearch.css';

/**
 * Main TwitterSearch component
 * Coordinates search, filtering, and display of tweets
 */
const TwitterSearch = ({ allTweets, darkMode, onTweetUpdate, isInitialLoading }) => {
  const [loadingCardExiting, setLoadingCardExiting] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [similarTweets, setSimilarTweets] = useState(null);
  const [similarityIndex, setSimilarityIndex] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDevMode } = useDevMode();
  const { addItem, setTrayOpen } = useStagingTray();

  // Get initial search term from URL
  const initialSearchTerm = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('q');
    if (searchParam && !params.get('similar')) {
      return decodeURIComponent(searchParam);
    }
    return '';
  }, []); // Only run on mount

  // Initialize search hook with all tweets and initial search term
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentSort,
    setCurrentSort,
    displayedTweets,
    filteredTweets,
    handleShuffle,
    handleLoadMore,
    getDisplayQuery
  } = useTwitterSearch(allTweets, { tweetsPerPage: 10, initialSearchTerm });
  
  // Create debounced function to update URL when search changes
  const updateSearchURL = useMemo(
    () => debounce((search) => {
      const params = new URLSearchParams(location.search);
      if (search) {
        params.set('q', search);
      } else {
        params.delete('q');
      }
      navigate(`/?${params.toString()}`, { replace: true });
    }, 300),
    [navigate, location.search]
  );

  // Handle search term update
  const handleSearchChange = (newSearchTerm) => {
    updateSearchURL(newSearchTerm);
  };
  
  // Load similarity index only when needed
  const loadSimilarityIndex = async () => {
    if (similarityIndex) return similarityIndex; // Return cached data

    try {
      const response = await fetch('/exports/insights/similarity_index.json');
      if (response.ok) {
        const data = await response.json();
        setSimilarityIndex(data);
        return data;
      }
    } catch (error) {
      console.error('Failed to load similarity index:', error);
    }
    return null;
  };

  // Handle similarity search from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const similarParam = params.get('similar');

    const handleSimilaritySearch = async () => {
      if (similarParam) {
        // Load similarity index when needed
        const indexData = await loadSimilarityIndex();

        if (indexData?.similarity_index) {
          const targetIds = decodeURIComponent(similarParam).split(',');

          // Find similar tweets based on the target IDs
          const allSimilarIds = new Set();
          const similarityScores = new Map();

          // Collect all similar tweet IDs and their scores
          targetIds.forEach(targetId => {
            if (indexData.similarity_index[targetId]) {
              indexData.similarity_index[targetId].similar_tweets.forEach(similar => {
                allSimilarIds.add(similar.tweet_id);
                // Keep the highest similarity score if tweet appears multiple times
                const currentScore = similarityScores.get(similar.tweet_id) || 0;
                if (similar.similarity_score > currentScore) {
                  similarityScores.set(similar.tweet_id, similar.similarity_score);
                }
              });
            }
          });

          // Filter and sort tweets by similarity
          const filteredSimilarTweets = allTweets
            .filter(tweet => allSimilarIds.has(tweet.id))
            .map(tweet => ({
              ...tweet,
              similarity_score: similarityScores.get(tweet.id) || 0
            }))
            .sort((a, b) => b.similarity_score - a.similarity_score);

          setSimilarTweets(filteredSimilarTweets);

          // Clear regular search when showing similar tweets
          setSearchTerm('');
        }
      } else {
        // Clear similar tweets if no similar parameter
        setSimilarTweets(null);
      }
    };

    handleSimilaritySearch();
  }, [location.search, allTweets]);

  // Restore staged saves from shared link (?staged=id1,id2,...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stagedParam = params.get('staged');
    if (!stagedParam || !allTweets || allTweets.length === 0) return;

    const targetIds = new Set(decodeURIComponent(stagedParam).split(','));
    const itemsToStage = allTweets.filter(tweet => targetIds.has(tweet.id));

    if (itemsToStage.length > 0) {
      itemsToStage.forEach(item => addItem(item));
      setTrayOpen(true);
    }

    // Remove staged param from URL so refreshing doesn't re-add
    params.delete('staged');
    const remaining = params.toString();
    navigate(remaining ? `/?${remaining}` : '/', { replace: true });
  }, [allTweets, location.search]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      updateSearchURL.cancel();
    };
  }, [updateSearchURL]);

  // Handle loading completion with exit animation - only for initial load
  useEffect(() => {
    if (!isInitialLoading && allTweets.length > 0 && !hasEverLoaded) {
      setLoadingCardExiting(true);
      setHasEverLoaded(true);
    }
  }, [isInitialLoading, allTweets.length, hasEverLoaded]);
  
  // Handle tweet updates (for dev mode)
  const handleTweetUpdate = async (updatedTweet, tweetSource) => {
    if (!isDevMode) return;

    try {
      // Determine source from parameter or tweet object
      const source = tweetSource || updatedTweet.source;

      if (process.env.REACT_APP_MODE === 'development') {
        console.log('[TwitterSearch] Handling tweet update:', {
          tweetId: updatedTweet.id,
          source,
          hasContent: !!updatedTweet.text,
          hasAuthor: !!updatedTweet.user?.name
        });
      }

      // For likes and roam, update via InkQuote API
      if (source === 'like' || isRoamSource(source)) {
        const content = updatedTweet.text || updatedTweet.full_text;
        const author = updatedTweet.user?.name;

        if (process.env.REACT_APP_MODE === 'development') {
          console.log('[TwitterSearch] Updating via InkQuote API:', {
            tweetId: updatedTweet.id,
            content: content?.substring(0, 50) + '...',
            author
          });
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/quotes/${updatedTweet.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content, author }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Server responded with ${response.status}`);
        }

        if (process.env.REACT_APP_MODE === 'development') {
          console.log('[TwitterSearch] InkQuote API update successful');
        }
      } else {
        // For bookmarks and user_tweets, update JSON file directly
        const fileMap = {
          'bookmark': '/exports/bookmarks/bookmarks.json',
          'user_tweet': '/exports/user_tweets/20241015_user_tweets.json'
        };

        const filePath = fileMap[source];
        if (!filePath) {
          throw new Error(`Unknown tweet source: ${source}`);
        }

        if (process.env.REACT_APP_MODE === 'development') {
          console.log('[TwitterSearch] Updating JSON file:', filePath);
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/update-tweet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tweet: updatedTweet,
            filePath: filePath
          }),
        });

        if (!response.ok) {
          const responseText = await response.text();
          throw new Error(`Server responded with ${response.status}: ${responseText}`);
        }

        if (process.env.REACT_APP_MODE === 'development') {
          console.log('[TwitterSearch] JSON file update successful');
        }
      }

      // Update local state
      onTweetUpdate(updatedTweet);

      return true; // Signal success to the TweetCard
    } catch (error) {
      console.error('[TwitterSearch] Failed to update tweet:', error);
      alert(`Failed to save tweet update: ${error.message}`);
      throw error; // Propagate error to TweetCard
    }
  };
  
  return (
    <div className={`twitter-search mt-8 ${darkMode ? 'dark-mode' : ''}`}>
      {/* Search bar with filters and suggestions */}
      <SearchBar
        allTweets={allTweets}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearchChange}
      />
      
      {/* Stats display */}
      <SearchStatsBar
        allTweets={allTweets}
        filteredTweets={similarTweets || filteredTweets}
        filteredCount={similarTweets ? similarTweets.length : filteredTweets.length}
        searchTerm={similarTweets ? "Similar saves" : searchTerm}
        displayQuery={similarTweets ? "Showing similar saves" : getDisplayQuery(searchTerm)}
      />
      
      {/* Shuffle and sort controls */}
      <SearchControls
        onShuffle={handleShuffle}
        currentSort={currentSort}
        onSortChange={setCurrentSort}
      />
      
      {/* Tweet list with pagination and loading states */}
      <div className="tweet-list-container">
        {/* Loading card - shows only during very first page load and exits with animation */}
        {(isInitialLoading && !hasEverLoaded) || loadingCardExiting ? (
          <LoadingCard isExiting={loadingCardExiting} />
        ) : null}
        
        {/* Show skeleton cards during initial loading only */}
        {isInitialLoading && !hasEverLoaded && (
          <div className="skeleton-cards">
            {[...Array(5)].map((_, index) => (
              <SkeletonCard key={index} delay={index * 100} />
            ))}
          </div>
        )}
        
        {/* Real content - fades in when loading completes */}
        {!isInitialLoading && (
          <TweetList
            tweets={similarTweets || displayedTweets}
            onLoadMore={similarTweets ? null : handleLoadMore}
            hasMoreTweets={similarTweets ? false : displayedTweets.length < filteredTweets.length}
            onTweetUpdate={handleTweetUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default TwitterSearch;